import {
  resolveLipSyncGenerationError,
  resolveLipSyncNotifyError,
} from "~/utils/lipSyncApiMessages.js";
import {
  isLoggedInCreditsInsufficient,
  trackInsufficientCredits,
  trackMediaComplianceFail,
} from "~/utils/lipsyncAnalytics.js";
import { openPayDialog } from "~/utils/pricingAnalytics.js";

const NON_GENERATE_FAIL_CREATE_CODES = new Set([
  100097,
  100100,
  100013,
  100014,
  100101,
  100401,
  100402,
]);

export function resolveEstimatedCredits(estimatedCredits) {
  if (estimatedCredits == null) return 0;
  if (typeof estimatedCredits === "object" && "value" in estimatedCredits) {
    return Number(estimatedCredits.value) || 0;
  }
  return Number(estimatedCredits) || 0;
}

export function shouldLogGenerateFailForCreateCode(code) {
  return !NON_GENERATE_FAIL_CREATE_CODES.has(Number(code));
}

const POLITICAL_SENSITIVE_COMPLIANCE_OUTCOMES = new Set([
  "political_sensitive_text",
  "political_sensitive_image",
]);

export function resolveMediaComplianceMessage(outcome, { toast, t, mediaType = "video" }) {
  if (outcome === "political_sensitive_text") {
    return resolveLipSyncGenerationError({ res: { code: 100401 }, t });
  }
  if (outcome === "political_sensitive_image") {
    return resolveLipSyncGenerationError({ res: { code: 100402 }, t });
  }

  const isImage = mediaType === "image";
  if (outcome === "rejected") {
    return toast(isImage ? "imageCheckRejected" : "videoCheckRejected");
  }
  if (outcome === "timeout") {
    return toast(isImage ? "imageCheckTimeout" : "videoCheckTimeout");
  }
  const failedKey = isImage ? "imageCheckFailed" : "videoCheckFailed";
  return (
    resolveLipSyncNotifyError({
      t,
      fallback: toast(failedKey),
    }) || toast(failedKey)
  );
}

/** 登录用户：前端预估积分不足则拦截并上报 220017 */
export function tryRejectLoggedInInsufficientCredits({
  isLoggedIn,
  userStore,
  estimatedCredits,
  t,
  notifyApi,
  sendLog,
  logCtx,
  track,
  onRejected,
}) {
  const loggedIn = Boolean(isLoggedIn?.value ?? isLoggedIn);
  if (!loggedIn) return false;

  const needed = resolveEstimatedCredits(estimatedCredits);
  if (!isLoggedInCreditsInsufficient(userStore, needed)) return false;

  const msg = resolveLipSyncGenerationError({ res: { code: 100100 }, isLoggedIn: true, t });
  notifyApi("warning", msg);
  trackInsufficientCredits(
    sendLog,
    {
      source: "frontend_precheck",
      estimated_credits: needed,
      available_credits: Number(userStore?.credits) || 0,
      massage: msg,
    },
    logCtx(),
  );
  openPayDialog(userStore, {
    triggerType: "pop",
    from: "generator_insufficient_credits",
  });
  onRejected?.();
  return true;
}

/** create-task 返回 100097 / 100100：上报 220017，不上报 220010 */
export function tryHandleCreateTaskInsufficientCredits({
  res,
  isLoggedIn,
  userStore,
  estimatedCredits,
  t,
  notifyApi,
  sendLog,
  logCtx,
  track,
  requestLogin,
  onRejected,
  stopGenerationProgress,
  clearTrackedGeneration,
}) {
  const code = Number(res?.code);
  if (code !== 100097 && code !== 100100) return false;

  stopGenerationProgress?.();
  clearTrackedGeneration?.();
  onRejected?.();

  const loggedIn = Boolean(isLoggedIn?.value ?? isLoggedIn);
  const msg = resolveLipSyncGenerationError({ res, isLoggedIn: loggedIn, t });
  notifyApi("warning", msg);
  trackInsufficientCredits(
    sendLog,
    {
      source: code === 100097 ? "api_100097" : "api_100100",
      estimated_credits: resolveEstimatedCredits(estimatedCredits),
      ...(loggedIn ? { available_credits: Number(userStore?.credits) || 0 } : {}),
      massage: msg,
    },
    logCtx(),
  );

  // Logged-in: both free-quota (100097) and insufficient credits (100100) open pay.
  // Anonymous: prompt login (pay dialog requires auth via openPayDialog anyway).
  if (loggedIn) {
    openPayDialog(userStore, {
      triggerType: "pop",
      from:
        code === 100097
          ? "generator_free_quota"
          : "generator_insufficient_credits",
    });
  } else {
    const from =
      code === 100097 ? "generator_submit_auth" : "generator_insufficient_credits";
    requestLogin?.("pop", from);
    track("0011", {
      type: "login_dialog_open",
      trigger_type: "pop",
      from,
    });
  }

  return true;
}

/** create-task 返回 100401 / 100402：政治敏感内容校验失败 */
export function tryHandleCreateTaskPoliticalSensitive({
  res,
  t,
  notifyApi,
  stopGenerationProgress,
  clearTrackedGeneration,
  onRejected,
}) {
  const code = Number(res?.code);
  if (code !== 100401 && code !== 100402) return false;

  stopGenerationProgress?.();
  clearTrackedGeneration?.();
  onRejected?.();

  const msg = resolveLipSyncGenerationError({ res, t });
  notifyApi("warning", msg);
  return true;
}

/** 图片/视频校验不通过：上报 220018，不上报 220010 */
export function abortSubmitAfterMediaComplianceCheck({
  message,
  taskId = "",
  imageUrl = "",
  videoUrl = "",
  outcome = "",
  notifyApi,
  sendLog,
  logCtx,
  stopGenerationProgress,
  clearTrackedGeneration,
  onRejected,
  resetGenerateStartedAt,
}) {
  stopGenerationProgress?.();
  clearTrackedGeneration?.();
  onRejected?.();
  notifyApi("warning", message);
  if (!POLITICAL_SENSITIVE_COMPLIANCE_OUTCOMES.has(outcome)) {
    trackMediaComplianceFail(
      sendLog,
      {
        task_id: taskId,
        outcome,
        massage: message || "",
        ...(imageUrl ? { image_url: imageUrl } : {}),
        ...(videoUrl ? { video_url: videoUrl } : {}),
      },
      logCtx(),
    );
  }
  resetGenerateStartedAt?.();
}
